import { useState, useMemo } from "react";
import { SortingState } from "@tanstack/react-table";

import { DataGrid, TableItemsNotFound } from "ui/data-grid";
import VersionEditModal, {
  FormType,
} from "components/modals/version-edit-modal/version-edit-modal";
import ConfirmArchiveModal from "components/modals/confirm-archive-modal/confirm-archive-modal";
import ConfirmUpdateMergeTagsModal from "components/modals/confirm-update-merge-tags-modal/confirm-update-merge-tags-modal";
import { ConfirmationDialog } from "ui/confirmation-dialog";
import { CloneTouchpointModal } from "components/modals/clone-touchpoint-modal/clone-touchpoint-modal";
import CloneProgramTemplatesModal from "components/modals/clone-program-templates-modal/clone-program-templates-modal";
import type { DisplayedProgramTemplate } from "containers/admin/clients/client/program-templates-page/components/program-templates-table/program-templates-table.helper";
import { CreateParentTouchpointModal } from "components/modals/create-parent-touchpoint-modal/create-parent-touchpoint-modal";
import UpdateClientThemeModal from "components/modals/update-client-theme-modal/update-client-theme-modal";

import { useGetTouchpointVersionStatusesQuery } from "hooks/api/dictionary";
import { useCurrentUser } from "hooks/api/use-current-user";
import { useBatches } from "hooks/api/use-batches";
import type { TouchpointVersionAttributes } from "hooks/api/use-touchpoint-versions";
import { useTouchpointVersions } from "hooks/api/use-touchpoint-versions";

import useTouchpointVersionsTable from "containers/admin/clients/touchpoint/tab-components/version-history-tab/hooks/use-touchpoint-versions-table";
import useVersionsActionMenuItems from "containers/admin/clients/touchpoint/tab-components/version-history-tab/hooks/use-versions-action-menu-items";
import { createTouchpointVersionTableColumns } from "containers/admin/clients/touchpoint/tab-components/version-history-tab/components/touchpoint-version-table/touchpoint-version-table-columns";
import { useFeatureFlag } from "contexts/feature-flag-context";
import { hasPermission } from "utilities/user";

import { TouchpointType } from "types/touchpoint";
import { Permission } from "types/auth";
import { PROGRAM_TEMPLATES_CAMPAIGN_NAME } from "utilities/constants";

interface TouchpointVersionTableProps {
  touchpointType: TouchpointType;
  creativeName: string;
  setSelectedVersion: (version: TouchpointVersionAttributes) => void;
}

function TouchpointVersionTable({
  touchpointType,
  creativeName,
  setSelectedVersion,
}: Readonly<TouchpointVersionTableProps>) {
  const currentUser = useCurrentUser();

  const isBatchManagementEnabled = useFeatureFlag("batch-management");
  const { data: touchpointVersionStatuses = [] } = useGetTouchpointVersionStatusesQuery();

  const {
    currentClient,
    currentCampaign,
    currentTouchpoint,
    // Modals
    newDraftModal,
    editVersionModal,
    confirmArchiveModal,
    confirmUpdateMergeTagsModal,
    isUpdatingMergeTags,
    cloneTouchpointModal,
    confirmPublishModal,
    // Quick actions
    openTouchpointEdit,
    openTouchpointPreview,
    // More actions menu handlers
    handleSubmitEditVersion,
    handleSubmitNewDraft,
    handlePublishVersion,
    handleArchiveVersion,
    handleConfirmUpdateMergeTags,
    isCloneTemplateOpen,
    setIsCloneTemplateOpen,
    isCreateParentTouchpointModalOpen,
    setIsCreateParentTouchpointModalOpen,
    isUpdateClientThemeModalOpen,
    setIsUpdateClientThemeModalOpen,
  } = useTouchpointVersionsTable(setSelectedVersion);

  const { data: batchPage } = useBatches(
    { clientId: currentClient.id },
    { enabled: isBatchManagementEnabled && Boolean(currentClient.id) },
  );
  const batches = useMemo(() => batchPage?.items ?? [], [batchPage?.items]);

  const isClientUserForBatchLink = hasPermission(currentUser, Permission.PERM_CLIENT_USER);

  const {
    // Actions menu handlers
    selectedVersion,
    currentActionItem,
    setCurrentActionItem,
    setSelectedVersion: setActionMenuSelectedVersion,
    handleCreateDraftFromVersion,
  } = useVersionsActionMenuItems({ newDraftModal });

  // Sorting
  const [sortingParams, setSortingParams] = useState<SortingState>([{ id: "version", desc: true }]);

  // Pagination
  const [pageIndex, setPageIndex] = useState(0);
  const [{ pageSize = 25 }, setAmount] = useState<{
    pageSize: number;
    totalRecords: number;
  }>({
    pageSize: 25,
    totalRecords: 0,
  });

  const sort = useMemo(
    () => sortingParams.map((param) => `${param.id},${param.desc ? "desc" : "asc"}`).join(","),
    [sortingParams],
  );

  const { data: touchpointVersions } = useTouchpointVersions({
    clientId: currentClient.id,
    campaignId: currentCampaign.id,
    touchpointId: currentTouchpoint.id,
    page: pageIndex,
    size: pageSize,
    sort,
  });

  const pagesAmount = touchpointVersions?.totalPages ?? 0;
  const totalRecords = touchpointVersions?.totalItems ?? 0;

  // Create table columns
  const isTemplate = currentCampaign.name === PROGRAM_TEMPLATES_CAMPAIGN_NAME;

  const columns = useMemo(
    () =>
      createTouchpointVersionTableColumns({
        touchpointVersionStatuses,
        openTouchpointEdit,
        openTouchpointPreview,
        touchpointType,
        isStudentPopulation: currentTouchpoint.populationType === "STUDENT",
        permissionToPublish: hasPermission(currentUser, Permission.PERM_TOUCHPOINT_PUBLISH),
        isTemplate,
        batches,
        clientId: currentClient.id,
        isClientUser: isClientUserForBatchLink,
        isBatchManagementEnabled,
        // Modal controls
        onSetCurrentActionItem: setCurrentActionItem,
        onSetSelectedVersion: setActionMenuSelectedVersion,
        onOpenConfirmArchiveModal: confirmArchiveModal.onOpen,
        onOpenConfirmUpdateMergeTagsModal: confirmUpdateMergeTagsModal.onOpen,
        isUpdatingMergeTags,
        onOpenCloneTouchpointModal: cloneTouchpointModal.onOpen,
        onOpenCloneTemplateModal: () => setIsCloneTemplateOpen(true),
        onOpenConfirmPublishModal: confirmPublishModal.onOpen,
        onOpenEditVersionModal: editVersionModal.onOpen,
        onOpenNewDraftModal: handleCreateDraftFromVersion,
        onOpenCreateParentTouchpointModal: () => setIsCreateParentTouchpointModalOpen(true),
        onOpenUpdateClientThemeModal: () => setIsUpdateClientThemeModalOpen(true),
      }),
    [
      openTouchpointEdit,
      openTouchpointPreview,
      batches,
      currentClient.id,
      currentUser,
      isClientUserForBatchLink,
      isBatchManagementEnabled,
      touchpointVersionStatuses,
      touchpointType,
      isTemplate,
      currentTouchpoint.populationType,
      setCurrentActionItem,
      setActionMenuSelectedVersion,
      confirmArchiveModal.onOpen,
      confirmUpdateMergeTagsModal.onOpen,
      isUpdatingMergeTags,
      cloneTouchpointModal.onOpen,
      setIsCloneTemplateOpen,
      confirmPublishModal.onOpen,
      editVersionModal.onOpen,
      handleCreateDraftFromVersion,
      setIsCreateParentTouchpointModalOpen,
      setIsUpdateClientThemeModalOpen,
    ],
  );

  const isEmail = touchpointType === TouchpointType.EMAIL;

  return (
    <div className="min-h-0 pb-6">
      <div className="h-full overflow-hidden">
        {touchpointVersions?.items?.length ? (
          <DataGrid
            columns={columns}
            data={touchpointVersions?.items || []}
            isLoading={false}
            manualSorting={false}
            showingRecordsIsOn={true}
            sortingParams={sortingParams}
            setSortingParams={setSortingParams}
            viewByAmountSelectIsOn={true}
            totalRecords={totalRecords}
            pageSize={pageSize}
            pagesAmount={pagesAmount}
            setAmount={setAmount}
            pageIndex={pageIndex}
            setPageIndex={(pageIndex) => setPageIndex(pageIndex)}
          />
        ) : (
          <TableItemsNotFound />
        )}
      </div>

      <VersionEditModal
        {...editVersionModal}
        id={currentActionItem?.id}
        isEmailType={isEmail}
        placeholderNotes={currentActionItem?.description}
        parentName={currentCampaign.name}
        formType={FormType.EDIT}
        applyClientTheme={currentActionItem?.applyClientTheme}
        name={creativeName}
        onSubmit={handleSubmitEditVersion}
        saveText="Save"
        touchpointId={currentTouchpoint.id}
        fiscalYear={currentActionItem?.fiscalYear}
        copyCode={currentTouchpoint?.copyCode}
        isTemplate={isTemplate}
      />
      <VersionEditModal
        {...newDraftModal}
        id={currentActionItem?.id}
        isEmailType={isEmail}
        parentName={currentCampaign.name}
        formType={FormType.NEW_VERSION}
        name={creativeName}
        onSubmit={handleSubmitNewDraft}
        fiscalYear={currentActionItem?.fiscalYear}
        copyCode={currentTouchpoint?.copyCode}
        isTemplate={isTemplate}
      />
      <ConfirmArchiveModal
        {...confirmArchiveModal}
        currentTouchpoint={currentActionItem}
        onSubmit={() => handleArchiveVersion(currentActionItem)}
      />
      <ConfirmUpdateMergeTagsModal
        {...confirmUpdateMergeTagsModal}
        onSubmit={() => handleConfirmUpdateMergeTags(currentActionItem?.id)}
      />
      <CloneTouchpointModal {...cloneTouchpointModal} selectedVersion={selectedVersion} />
      {isCloneTemplateOpen && selectedVersion && (
        <CloneProgramTemplatesModal
          isOpen={isCloneTemplateOpen}
          onClose={() => setIsCloneTemplateOpen(false)}
          selectedTemplates={
            [
              {
                id: currentTouchpoint.id,
                name: currentTouchpoint.name,
                type: currentTouchpoint.type,
                latestVersion: {
                  version: selectedVersion.version ?? 0,
                  status: selectedVersion.status ?? "",
                  id: selectedVersion.id,
                  applyClientTheme: selectedVersion.applyClientTheme ?? false,
                },
              },
            ] as DisplayedProgramTemplate[]
          }
        />
      )}
      {isCreateParentTouchpointModalOpen && (
        <CreateParentTouchpointModal
          setIsOpen={setIsCreateParentTouchpointModalOpen}
          isOpen={isCreateParentTouchpointModalOpen}
          selectedVersion={selectedVersion}
        />
      )}
      <ConfirmationDialog
        open={confirmPublishModal.isOpen}
        onClose={confirmPublishModal.onClose}
        onConfirm={() => {
          handlePublishVersion(currentActionItem?.id);
        }}
        headline={
          touchpointType === "EMAIL"
            ? "Are you sure you want to publish this email to iterable"
            : "Are you sure you want to publish this landing page"
        }
        message={"This will replace the current published version. This action cannot be undone"}
        confirmButtonText={touchpointType === "EMAIL" ? "Yes, publish to iterable" : "Yes, publish"}
      />
      {isUpdateClientThemeModalOpen && (
        <UpdateClientThemeModal
          setIsOpen={setIsUpdateClientThemeModalOpen}
          isOpen={isUpdateClientThemeModalOpen}
          touchpointVersionId={selectedVersion?.id || ""}
          clientId={currentClient.id}
          campaignId={currentCampaign.id}
          touchpointId={currentTouchpoint.id}
        />
      )}
    </div>
  );
}

export default TouchpointVersionTable;
